import { Link } from "react-router-dom";
import { ArrowRight, Building2, Mountain, Building, Coins, Sparkles, Handshake } from "lucide-react";
import { LogoMarkStatic } from "@/components/LogoMark";
import SeoHead from "@/components/SeoHead";

const ASSET_FUNNELS = [
  {
    slug: "business",
    submitPath: "/submit-business",
    title: "Sell Your Business",
    sub: "List a profitable business for review by our verified network of acquirers who work with seller financing and creative structures.",
    icon: Building2,
    bg: "https://images.pexels.com/photos/22812019/pexels-photo-22812019.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    slug: "multifamily",
    submitPath: "/submit-multifamily",
    title: "Sell Your Multifamily Property",
    sub: "Submit a multifamily property to a network of acquirers exploring master lease, owner finance, and subject-to structures.",
    icon: Building,
    bg: "https://images.unsplash.com/photo-1505843795480-5cfb3c03f6ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZWFsJTIwZXN0YXRlJTIwYWVyaWFsJTIwdmlld3xlbnwwfHx8fDE3ODE1Njk3MzZ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    slug: "land",
    submitPath: "/submit-land",
    title: "Sell Your Land",
    sub: "Submit land to acquirers open to carry-back notes, installment sales, and partial financing.",
    icon: Mountain,
    bg: "https://images.unsplash.com/photo-1777058019384-fca55ee23102?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxmYXJtbGFuZCUyMGxhbmRzY2FwZSUyMHN1bnJpc2V8ZW58MHx8fHwxNzgxNTY5NzM2fDA&ixlib=rb-4.1.0&q=85",
  },
];

const BENEFITS = [
  { icon: Coins, title: "Seller Financing Solutions", body: "Earn passive income on your sale. Predictable monthly payments backed by your former asset." },
  { icon: Sparkles, title: "Creative Financing", body: "Subject-to, wrap mortgages, installment sales: we structure deals others cannot." },
  { icon: Handshake, title: "Master Lease Options", body: "Receive monthly checks while we manage and improve. Optional buyout at a pre-agreed price." },
];

export default function SellerFunnel() {
  return (
    <div className="min-h-screen bg-[#05070A] text-slate-100">
      <SeoHead route="/sell" />
      <nav className="sticky top-0 z-50 glass-nav">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <LogoMarkStatic className="h-20 md:h-[144px]" />
          </Link>
          <Link to="/login" className="text-sm text-slate-300 hover:text-emerald-400">Acquirer Login</Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute top-10 right-10 h-[400px] w-[600px] rounded-full bg-emerald-500/10 blur-[140px]" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-12">
          <div className="text-xs uppercase tracking-widest text-emerald-400 mb-4">Sellers · Owners · Heirs</div>
          <h1 className="font-heading font-bold text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-4xl">
            List your asset with<br />
            <span className="text-emerald-400">a network of acquirers.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-2xl">
            Acquigine connects sellers to a network of acquirers who use
            <span className="text-slate-200"> seller financing, master leases, and creative structures</span>.
            Submissions are reviewed before becoming visible to any acquirer, and may be declined.
          </p>
        </div>
      </section>

      {/* Asset cards */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 pb-20 grid md:grid-cols-3 gap-6">
          {ASSET_FUNNELS.map((a, i) => {
            const I = a.icon;
            return (
              <Link
                key={a.slug}
                to={a.submitPath}
                data-testid={`funnel-card-${a.slug}`}
                className="ae-card ae-card-hover overflow-hidden group fade-in-up"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={a.bg} alt={a.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
                  <I className="absolute bottom-4 left-4 h-7 w-7 text-emerald-400" strokeWidth={1.5} />
                </div>
                <div className="p-6">
                  <div className="font-heading text-2xl tracking-tight">{a.title}</div>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{a.sub}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-emerald-400 text-sm font-medium">
                    Submit your listing <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Benefits */}
      <section className="border-t border-white/8 bg-[#0A0D12]">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-xs uppercase tracking-widest text-emerald-400 mb-4">How it works</div>
          <h2 className="font-heading text-4xl tracking-tight max-w-2xl">Flexible terms. Reviewed submissions.</h2>
          <p className="text-sm text-slate-400 mt-3 max-w-2xl">
            Every submission is reviewed by our team before becoming visible to any acquirer on the platform. Submissions may be declined. If approved, verified acquirers reach out directly.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-px bg-white/8 border border-white/8">
            {BENEFITS.map((b) => {
              const I = b.icon;
              return (
                <div key={b.title} className="bg-[#0A0D12] p-8">
                  <I className="h-6 w-6 text-emerald-400 mb-4" strokeWidth={1.5} />
                  <div className="font-heading text-lg">{b.title}</div>
                  <div className="text-sm text-slate-400 mt-2 leading-relaxed">{b.body}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <footer className="border-t border-white/8 py-6 text-center text-xs text-slate-600">
        © {new Date().getFullYear()} Acquigine
      </footer>
    </div>
  );
}
